import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Calendar, History, Users, Info, Flower, Carrot } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Meta } from '../components/Meta';
import { CreateFieldModal } from '../components/Agriculture/CreateFieldModal';
import { europeanCountries } from '../lib/constants/countries';

interface Field {
  id: string;
  name: string;
  description?: string;
  country: string;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  owner_id: string;
  created_at: string;
  avatar_url?: string;
  owner?: {
    username: string;
    avatar_url?: string;
  };
  _count?: {
    members: number;
  };
}

interface Task {
  id: string;
  field_id: string;
  user_id: string;
  date: string;
  task_type: 'planting' | 'watering' | 'harvesting' | 'fertilising' | 'other';
  title: string;
  details?: string;
  created_at: string;
  user?: {
    username: string;
    avatar_url?: string;
  };
}

export function Agriculture() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [fields, setFields] = useState<Field[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  useEffect(() => {
    // Check authentication status
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
      if (session) {
        fetchFields();
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
      if (session) {
        fetchFields();
      } else {
        setFields([]);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

const fetchFields = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setLoading(false);
      return;
    }

    // Fetch fields owned by the user
    const { data: ownedFields, error: ownedFieldsError } = await supabase
      .from('fields')
      .select('*')
      .eq('owner_id', user.id);

    if (ownedFieldsError) throw ownedFieldsError;

    // Fetch fields where the user is a member
    const { data: memberFields, error: memberFieldsError } = await supabase
      .from('field_members')
      .select('field_id')
      .eq('user_id', user.id);

    if (memberFieldsError) throw memberFieldsError;

    const memberFieldIds = memberFields.map(member => member.field_id);

    // Fetch details of fields where the user is a member
    const { data: memberFieldDetails, error: memberFieldDetailsError } = await supabase
      .from('fields')
      .select('*')
      .in('id', memberFieldIds);

    if (memberFieldDetailsError) throw memberFieldDetailsError;

    // Combine owned and member fields
    const allFields = [...ownedFields, ...memberFieldDetails];

    // Get member counts for each field
    const fieldsWithCounts = await Promise.all(
      allFields.map(async (field) => {
        const { count } = await supabase
          .from('field_members')
          .select('*', { count: 'exact', head: true })
          .eq('field_id', field.id);

        return {
          ...field,
          _count: { members: (count || 0) + 1 } // +1 for owner
        };
      })
    );

    setFields(fieldsWithCounts);
  } catch (err: any) {
    console.error('Error fetching fields:', err);
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10 pt-0 pb-8 lg:py-8">
      {/* Mobile Full-Width Header */}
      <div className="lg:hidden relative h-64 overflow-hidden -mx-4 sm:-mx-6 -mt-8">
        <img
          src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/post-images/59bed50f-5ccf-4265-87fa-7743af34d361/planting.webp"
          alt="Design & Manage Your Garden Layout"
          className="w-full h-full object-cover shadow-none"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white"></div>
      </div>

      {/* Mobile Content Below Image */}
      <div className="lg:hidden px-4 pt-8 pb-12 text-center">
        <h1 className="text-3xl font-gelica font-bold text-content mb-4">
          {t('garden.title')}
        </h1>
        <p className="text-lg text-content/70 mb-6 max-w-md mx-auto">
          {t('garden.subtitle')}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={() => {
              if (isAuthenticated) {
                setShowCreateModal(true);
              } else {
                window.dispatchEvent(new CustomEvent('show-auth'));
              }
            }}
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-white bg-accent-text hover:bg-accent-text/90 transition-colors shadow-sm whitespace-nowrap"
          >
            {t('garden.startDesigning')}
          </button>
          <Link
            to="/practitioners?categories=Permaculture_Designer"
            className="inline-flex items-center px-6 py-3 border-2 border-accent-text text-base font-medium rounded-full text-accent-text hover:bg-accent-base/10 transition-colors whitespace-nowrap"
          >
            {t('garden.findExperts')}
          </Link>
        </div>
      </div>

      {/* Desktop Hero */}
      <div className="hidden lg:block relative mb-12 mt-8">
        <div className="py-8 lg:py-16">
          <div className="max-w-page mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <h1 className="text-4xl lg:text-5xl font-gelica leading-tight mb-6">
                  {t('garden.title')}
                </h1>
                <p className="text-lg lg:text-xl text-gray-600 mb-6">
                  {t('garden.subtitle')}
                </p>
                <div className="mb-6 flex gap-4 items-center">
                  <button
                    onClick={() => {
                      if (isAuthenticated) {
                        setShowCreateModal(true);
                      } else {
                        window.dispatchEvent(new CustomEvent('show-auth'));
                      }
                    }}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-full text-white bg-accent-text hover:bg-accent-text/90 transition-colors whitespace-nowrap"
                  >
                    {t('garden.startDesigning')}
                  </button>
                  <Link
                    to="/practitioners?categories=Permaculture_Designer"
                    className="inline-flex items-center px-6 py-3 border-2 border-accent-text text-base font-medium rounded-full text-accent-text hover:bg-accent-base/10 transition-colors whitespace-nowrap"
                  >
                    {t('garden.findExperts')}
                  </Link>
                </div>
              </div>
              <div className="hidden lg:block">
                <img 
                  src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/post-images/59bed50f-5ccf-4265-87fa-7743af34d361/planting.webp" 
                  alt="Agriculture Management"
                  className="w-full h-auto blob-shape"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Meta 
        title="My Garden - Garden Management Tool"
        description="Create and manage your gardens, track activities, and collaborate with others."
      />

      <div className="flex justify-between items-center mb-8 mt-8 lg:mt-0">
        <h1 className="text-2xl font-bold text-content">{t('garden.myGardens')}</h1>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setShowInfo(!showInfo)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-accent-text hover:bg-accent-base/10 rounded-md"
          >
            <Info className="h-4 w-4 mr-2" />
            {showInfo ? 'Hide Information' : 'How to use this tool'}
          </button>
          {isAuthenticated && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-accent-text hover:bg-accent-text/90"
              data-create-garden
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Garden
            </button>
          )}
        </div>
      </div>

      {showInfo && (
        <div className="max-w-4xl mx-auto mb-12 animate-fade-in">
          <h2 className="text-xl font-semibold text-content mb-6 text-center">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 rounded-full bg-accent-base/20 flex items-center justify-center text-accent-text font-bold">1</div>
                <h3 className="ml-3 font-medium text-content">Create Your Field</h3>
              </div>
              <p className="text-content/70">Start by creating your garden and adding basic information like location and description.</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 rounded-full bg-accent-base/20 flex items-center justify-center text-accent-text font-bold">2</div>
                <h3 className="ml-3 font-medium text-content">Add Team Members</h3>
              </div>
              <p className="text-content/70">Invite family members or collaborators to join your field and work together.</p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 rounded-full bg-accent-base/20 flex items-center justify-center text-accent-text font-bold">3</div>
                <h3 className="ml-3 font-medium text-content">Track Activities</h3>
              </div>
              <p className="text-content/70">Log and manage field tasks like planting, watering, and harvesting in a shared calendar.</p>
            </div>
          </div>
          
          {/* Video Tutorial Section */}
          <div className="mt-8 bg-gradient-to-r from-accent-text/5 to-accent-text/10 rounded-xl p-6 border border-accent-text/10">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-content mb-2">Complete Process Overview</h3>
                <p className="text-content/70 mb-4">
                  Watch this comprehensive video guide that walks through the entire agricultural management process from start to finish.
                </p>
              </div>
            </div>
            <a
              href="https://www.youtube.com/watch?v=KsrbxM-2x98"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm"
            >
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              Watch Complete Tutorial
            </a>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-text"></div>
        </div>
      ) : fields.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Calendar className="h-12 w-12 text-content/20 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-content mb-2">No Gardens Yet</h2>
          <p className="text-content/60 mb-6">
            Create your first garden to start tracking activities and collaborating with others.
          </p>
          {isAuthenticated ? (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-accent-text hover:bg-accent-base/10 rounded-md"
              data-create-garden
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Garden
            </button>
          ) : (
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('show-auth'))}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-accent-text hover:bg-accent-base/10 rounded-md"
            >
              <Plus className="h-4 w-4 mr-2" />
              Sign in to Create Garden
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {fields.map((field) => (
            <div
              key={field.id}
              className="relative rounded-lg shadow-sm hover:shadow-md transition-all overflow-hidden group"
            >
              {/* Background Image */}
              <div className="absolute inset-0 w-full h-full">
                <img 
                  src="https://afvltpqnhmaxanirwnqz.supabase.co/storage/v1/object/public/post-images/59bed50f-5ccf-4265-87fa-7743af34d361/fieldbg.png" 
                  alt="" 
                  className="w-full h-full object-cover opacity-100"
                />
                <div className="absolute inset-0 bg-white/90 "></div>
              </div>
              
              <div className="p-6">
                <div className="flex items-center gap-3 mb-2 relative z-10">
                  {field.avatar_url ? (
                    <img
                      src={field.avatar_url}
                      alt={field.name}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-accent-text/10 flex items-center justify-center">
                      {(field.id.charCodeAt(0) % 2 === 0) ? (
                        <Flower className="h-5 w-5 text-accent-text" />
                      ) : (
                        <Carrot className="h-5 w-5 text-accent-text" />
                      )}
                    </div>
                  )}
                  <h3 className="text-lg font-semibold text-content">
                    {field.name}
                  </h3>
                </div>
                {field.description && (
                  <p className="text-content/80 mb-4 line-clamp-2 relative z-10">
                    {field.description}
                  </p>
                )}
                <div className="flex items-center justify-between text-sm text-content/60 relative z-10">
                  <div className="flex items-center">
                    <Users className="h-4 w-4 mr-1" />
                    <span>{field._count?.members || 1} members</span>
                  </div>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    <span>
                      {new Date(field.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 relative z-10 border-t border-accent-text/10">
                <div className="flex items-center justify-between relative z-10">
                  <div className="flex items-center text-sm text-content/60">
                    <Info className="h-4 w-4 mr-1" />
                    <span>
                      {europeanCountries.find(c => c.value === field.country)?.label || field.country}
                    </span>
                  </div>
                  <button
                    onClick={() => navigate(`/gardens/${field.id}`)}
                    className="inline-flex items-center px-3 py-1 text-sm text-accent-text hover:bg-accent-text hover:text-white rounded-md transition-colors"
                  >
                    View Garden
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateFieldModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchFields();
          }}
        />
      )}
    </div>
  );
}

export default Agriculture;